import { Map } from 'immutable'
import fetchJSON from 'lib/fetchJSON'
import { fulfilled, pending } from 'lib/promiseMiddlewareHelpers'
import { deserializeRoute } from 'lib/api/records'
import _ from 'lodash'

// Actions
export const LOAD_ROUTE = 'routes/LOAD_ROUTE'
export const UPDATE_ROUTE = 'routes/UPDATE_ROUTE'
export const ADD_PHOTO = 'routes/ADD_PHOTO'
export const PHOTO_REMOVED = 'routes/PHOTO_REMOVED'
export const PIN_ROUTE = 'routes/PIN_ROUTE'
export const UNPIN_ROUTE = 'routes/UNPIN_ROUTE'
export const DELETE_ROUTE = 'routes/DELETE_ROUTE'
export const BATCH_SET_VISIBILITY = 'routes/BATCH_SET_VISIBILITY'
export const BATCH_DELETE = 'routes/BATCH_DELETE'
export const BATCH_ARCHIVE = 'routes/BATCH_ARCHIVE'
export const BATCH_UNARCHIVE = 'routes/BATCH_UNARCHIVE'

// APIv1 returns POI using reasonable attribute naming that matches the columns
// in our database. APIv2, which the majority of rwgps-ui was written agains,
// uses a mongodb-optimized single letter attribute naming scheme. However,
// since we've moved POIs out of Mongodb, we now use ActiveRecord's aliasing to
// convert the reasonable mysql column names into APIv2 single-character names.
// APIv3 attempts to fix this by conforming rails output to the mysql naming
// scheme again via a serializer.
//
// it is with great shame and self-loathing that I am down-converting APIv3
// output here to match the APIv2 output (so that it can then be converted to
// APIv1 output by individual components). I'll fix it as soon as I can, until
// then I promise I'll buy you a beer if you get bitten by this insanity
// (@purdyk has likened the situation to "a snake-shooting gun... made out of
// SNAKES")

// eslint-disable-next-line camelcase
const convertV3POIToV2 = ({ id, lat, lng, url, photo_ids, description, name, poi_type, user_id, visibility }) => ({
  id,
  lat,
  lng,
  n: name,
  t: poi_type,
  d: description,
  url,
  pids: photo_ids,
  v: visibility,
  uid: user_id
})

// Reducer
export const reducer = (state = Map(), action = {}) => {
  switch (action.type) {
    case fulfilled(LOAD_ROUTE): {
      const { payload } = action
      // this is a little messy, but it serves as a simple and effective APIv3
      // "compatibility layer" that will hold until we have enough APIv3 coverage
      // in place to justify using something like normalizr
      const payloadUser = payload.extras.find(e => e.type === 'user' && e.id === payload.route.user_id)
      const payloadEvent = payload.extras.find(e => e.type === 'event' && e.id === payload.route.event_id)
      const payloadClub = payload.extras.find(e => e.type === 'club' && e.club.user_id === payload.route.user_id)
      const payloadMapPdf = payload.extras.find(e => e.type === 'map_pdf')
      // fallback to empty object to prevent crashes if the backend messes up the user
      // (see https://github.com/ridewithgps/ridewithgps/issues/5683#issuecomment-413917095 for an example)
      const route = deserializeRoute(payload.route)
        .set('user', payloadUser ? payloadUser.user : { name: 'unknown' })
        .set('event', payloadEvent ? payloadEvent.event : null)
        .set('club', payloadClub ? payloadClub.club : null)
        .set('mapPdf', payloadMapPdf ? payloadMapPdf.map_pdf : null)
        .set('pointsOfInterest', payload.extras
          .filter(e => e.type === 'point_of_interest')
          .map(({ point_of_interest: poi }) => convertV3POIToV2(poi))
        )
        .set('photos', payload.route.photo_ids
          .map(id => payload.extras.find(e => e.type === 'photo' && e.id === id))
          .filter(v => v)
          .map(e => e.photo)
        )
        .set('segments', payload.extras
          .filter(e => e.type === 'segment_match')
          .map(({ segment_match: segmentMatch }) => segmentMatch)
        )
        .set('pinned', !!payload.extras.find(e => e.type === 'favorite'))
        .set('_permissions', payload.permissions)

      return state.set(payload.route.id, route)
    }
    case fulfilled(ADD_PHOTO): {
      const { payload } = action
      const { routeId } = action.meta
      const route = state.get(routeId)
      if (!route) { return state }
      return state.setIn([routeId, 'photos'], [payload.photo].concat(route.photos))
    }
    case PHOTO_REMOVED: {
      // this one is a little funky because the Gallery is what actually made the API
      // call (hence why there's no fulfilled() helper), we just need to sync
      // the photo deletion back to our in-memory copy of the route
      const { routeId, photoId } = action.meta
      const route = state.get(routeId)
      if (!route) { return state }
      return state.setIn([routeId, 'photos'], route.photos.filter(({ id }) => id !== photoId))
    }
    case pending(PIN_ROUTE): {
      const { routeId } = action.meta
      return state.setIn([routeId, 'pinned'], true)
    }
    case pending(UNPIN_ROUTE): {
      const { routeId } = action.meta
      return state.setIn([routeId, 'pinned'], false)
    }
    case pending(UPDATE_ROUTE): {
      const { routeId, update } = action.meta
      const route = state.get(routeId)

      const newRoute = route.withMutations(r => {
        r.merge(update)
        update.rawPoints && r.set('rawPoints', update.rawPoints) // We are doing this so we preserve rawPoints being an array
        update.trackPoints && r.set('trackPoints', update.trackPoints) // We are doing this so we preserve trackPoints being an array
      })

      return state.set(routeId, newRoute)
    }
    default:
      return state
  }
}

export const serializeRouteUpdate = (update) => {
  const serialized = {}
  const propertyMappings = {
    pavementType: 'pavement_type_id',
    visibility: 'visibility',
    name: 'name',
    description: 'description',
    recreationTypes: 'recreation_type_ids',
    trackPoints: 'track_points'
  }
  const propertyTransforms = {
    trackPoints: (value) => JSON.stringify(value)
  }

  // TODO: this area is rather messy and convoluted, here's an explanation of what's going on.
  // The server expects a JSON.stringified version of rawPoints (the one with single letter keys) in the 'track_points' property.
  // We keep those rawPoints in the route.rawPoints in the redux store, and they are partnered with route.trackPoints (which have been run through metricsCalculator)
  // The following code is just going to do a swap-a-roo to make this all work.
  const updateWithCorrectTrackPoints = ((update) => {
    if (Object.keys(update).includes('trackPoints') && Object.keys(update).includes('rawPoints')) {
      const newUpdate = Object.assign({}, update)
      newUpdate.trackPoints = newUpdate.rawPoints
      return _.omit(newUpdate, 'rawPoints')
    }
    return update
  })(update)

  Object.keys(updateWithCorrectTrackPoints).forEach(key => {
    const serverKey = propertyMappings[key]
    if (!serverKey) {
      console.error(`unknown key '${key}' provided in route update`)
    }
    const transform = propertyTransforms[key] || (v => v) // either we have a transform or we don't modify anything
    serialized[serverKey] = transform(updateWithCorrectTrackPoints[key])
  })
  return serialized
}

export const loadRoute = (routeId, privacyCode = '') => ({
  type: LOAD_ROUTE,
  payload: fetchJSON(`/routes/${routeId}.json?apikey=x&version=3${privacyCode ? `&privacy_code=${privacyCode}` : ''}`)
})

export const addPhoto = (routeId, photo) => {
  const body = new window.FormData()
  body.append('file', photo)
  body.append('parent_type', 'route')
  body.append('parent_id', routeId)
  const payload = fetchJSON('/photos.json', null, {
    method: 'POST',
    body,
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  })

  return {
    type: ADD_PHOTO,
    payload,
    meta: { routeId }
  }
}

export const photoRemoved = (routeId, photoId) => ({
  type: PHOTO_REMOVED,
  meta: { routeId, photoId }
})

export const pinRoute = (routeId, params = {}) => ({
  type: PIN_ROUTE,
  payload: fetchJSON(`/remember/route/${routeId}.json`, params, { method: 'POST' }),
  meta: { routeId }
})

export const unpinRoute = (routeId) => ({
  type: UNPIN_ROUTE,
  payload: fetchJSON(`/forget/route/${routeId}.json`, {}, { method: 'POST' }),
  meta: { routeId }
})

export const updateRoute = (routeId, update) => ({
  type: UPDATE_ROUTE,
  payload: fetchJSON(`/routes/${routeId}.json`, { route: serializeRouteUpdate(update) }, { method: 'PATCH' }),
  meta: { routeId, update }
})

export const deleteRoute = (routeId) => ({
  type: DELETE_ROUTE,
  payload: fetchJSON(`/routes/${routeId}.json`, null, { method: 'DELETE' })
})

export const batchDelete = (routeIds) => ({
  type: BATCH_DELETE,
  payload: fetchJSON(`/routes/batch_destroy.json`, { route_ids: routeIds }, { method: 'POST' })
})

export const batchArchive = (routeIds) => ({
  type: BATCH_ARCHIVE,
  payload: fetchJSON(`/routes/batch_archive.json`, { route_ids: routeIds }, { method: 'POST' })
})

export const batchUnarchive = (routeIds) => ({
  type: BATCH_UNARCHIVE,
  payload: fetchJSON(`/routes/batch_unarchive.json`, { route_ids: routeIds }, { method: 'POST' })
})

// ids is an array of id ints, attr and value are strings
export const batchSetVisibility = (ids, visibility) => ({
  type: BATCH_SET_VISIBILITY,
  payload: fetchJSON('/routes/batch_set_attr.json', {
    attr: 'visibility',
    route_ids: ids.join(','),
    value: visibility
  }, { method: 'POST' })
})
